import RoutePageTitle from "components/RoutePageTitle";
/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import * as XLSX from "xlsx";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import { useTranslation } from "react-i18next";
import { useUserContext } from "context/UserContext";
import { getMdmAuthKeyByEmail } from "utils/getMdmAuthKeyByEmail";

const REMOTE_CRM_API_BASE = "https://crm-mqtt-718408119422.asia-east1.run.app";
const LOCAL_CRM_API_BASE = "http://127.0.0.1:8080";
const TAIPEI_TIME_ZONE = "Asia/Taipei";

const asArray = (value) => (Array.isArray(value) ? value : []);

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return formatValue(value);
  return date.toLocaleString("zh-TW", { hour12: false, timeZone: TAIPEI_TIME_ZONE });
};

const getCrmApiBase = () => {
  const configuredBase = process.env.REACT_APP_CRM_API_BASE?.trim();
  if (configuredBase) return configuredBase;

  if (typeof window !== "undefined") {
    const { hostname } = window.location;
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return LOCAL_CRM_API_BASE;
    }
  }

  return REMOTE_CRM_API_BASE;
};

const readApiResponse = async (response) => {
  const rawText = await response.text();
  let json = null;

  if (rawText) {
    try {
      json = JSON.parse(rawText);
    } catch (error) {
      const plainTextError = new Error(
        rawText.length > 160 ? `${rawText.slice(0, 157)}...` : rawText
      );
      plainTextError.responseText = rawText;
      plainTextError.status = response.status;
      throw plainTextError;
    }
  }

  return json;
};

const createDefaultHistoryRange = (now = new Date()) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TAIPEI_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  const end = `${values.year}-${values.month}-${values.day}`;
  const startDate = new Date(Date.UTC(Number(values.year), Number(values.month) - 7, 1));
  const lastDayOfStartMonth = new Date(
    Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth() + 1, 0)
  ).getUTCDate();
  startDate.setUTCDate(Math.min(Number(values.day), lastDayOfStartMonth));
  const start = startDate.toISOString().slice(0, 10);

  return { start, end };
};

const hasCompleteRange = (range) => Boolean(range?.start && range?.end);

const getRangeSummaryLabel = (range) =>
  hasCompleteRange(range) ? `${range.start} ~ ${range.end}` : "最近六個月";

const getSettlementQueryKey = (email, range) =>
  [email || "", range?.start || "", range?.end || ""].join("|");

const pickLatestBatchId = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return "";
  const latest = rows.reduce((currentLatest, row) => {
    if (!currentLatest) return row;
    const latestTime = new Date(currentLatest?.date || 0).getTime();
    const rowTime = new Date(row?.date || 0).getTime();
    return rowTime > latestTime ? row : currentLatest;
  }, null);
  return latest?.batchId || "";
};

const dateCell = (value) => (
  <MDTypography variant="caption" color="text" fontWeight="regular">
    {formatDate(value)}
  </MDTypography>
);

const primaryActionButtonSx = {
  minWidth: 110,
  fontSize: "0.8rem",
  fontWeight: 700,
  letterSpacing: "0.01em",
  color: "#fff",
  backgroundColor: "#1a73e8",
  borderColor: "#1a73e8",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#1557b0",
    borderColor: "#1557b0",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    color: "#fff",
    backgroundColor: "#9bbcf3",
    borderColor: "#9bbcf3",
  },
};

const secondaryActionButtonSx = {
  minWidth: 110,
  fontSize: "0.8rem",
  fontWeight: 700,
  letterSpacing: "0.01em",
  color: "#1a73e8",
  borderColor: "#1a73e8",
  backgroundColor: "#eef4ff",
  boxShadow: "none",
  "&:hover": {
    color: "#1557b0",
    borderColor: "#1557b0",
    backgroundColor: "#dde9ff",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    color: "#8aa3c7",
    borderColor: "#c7d6ee",
    backgroundColor: "#f5f8fc",
  },
};

const sanitizeDataTableValue = (value) => {
  if (React.isValidElement(value)) return value;
  if (Array.isArray(value)) return JSON.stringify(value);
  if (value && typeof value === "object") return JSON.stringify(value);
  return value;
};

const sanitizeDataTableRow = (row) =>
  Object.fromEntries(
    Object.entries(row || {}).map(([key, value]) => [key, sanitizeDataTableValue(value)])
  );

const flattenSettlementRows = (rows) =>
  asArray(rows).flatMap((row) =>
    asArray(row?.settlements).map((settlement) => ({
      batchDate: row?.date ?? "",
      batchId: row?.batchId ?? "",
      batchMessage: row?.message ?? "",
      ...settlement,
    }))
  );

function BillingReconcileCM() {
  const { t } = useTranslation("common");
  const { userData } = useUserContext() || {};
  const email = userData?.email ? userData.email.trim().toLowerCase() : "";
  const mdmAuthKey = useMemo(() => getMdmAuthKeyByEmail(email), [email]);
  const crmApiBase = useMemo(getCrmApiBase, []);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [latestBatchId, setLatestBatchId] = useState("");
  const [latestBatchError, setLatestBatchError] = useState("");
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const [selectedPayloadDetail, setSelectedPayloadDetail] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [autoCaptureEnabled, setAutoCaptureEnabled] = useState(false);
  const [autoCaptureLoading, setAutoCaptureLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [filters, setFilters] = useState(createDefaultHistoryRange);
  const [appliedRange, setAppliedRange] = useState(createDefaultHistoryRange);
  const [loadedQueryKey, setLoadedQueryKey] = useState("");
  const settlementRequestIdRef = useRef(0);

  const settlementRows = useMemo(() => flattenSettlementRows(rows), [rows]);
  const currentQueryKey = useMemo(
    () => getSettlementQueryKey(email, appliedRange),
    [appliedRange, email]
  );
  const canDownloadSettlements =
    !loading && settlementRows.length > 0 && loadedQueryKey === currentQueryKey;

  const fetchSettlements = useCallback(
    async (range = appliedRange) => {
      if (!email) {
        setRows([]);
        setLoadedQueryKey("");
        setError("找不到登入者 Email");
        return;
      }

      const requestId = settlementRequestIdRef.current + 1;
      settlementRequestIdRef.current = requestId;
      const queryKey = getSettlementQueryKey(email, range);
      setLoading(true);
      setError("");
      setRows([]);
      setLoadedQueryKey("");

      try {
        const requestBody = { email };
        if (hasCompleteRange(range)) {
          requestBody.start = range.start;
          requestBody.end = range.end;
        }

        const response = await fetch(`${crmApiBase}/api/v1/order/settle`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Mdm-Auth": mdmAuthKey,
          },
          body: JSON.stringify(requestBody),
        });

        const json = await readApiResponse(response);
        if (!response.ok) {
          throw new Error(json?.message || `Request failed (${response.status})`);
        }

        if (settlementRequestIdRef.current !== requestId) return;
        setRows(asArray(json?.data?.body));
        setLoadedQueryKey(queryKey);
      } catch (fetchError) {
        if (settlementRequestIdRef.current !== requestId) return;
        console.error(fetchError);
        setRows([]);
        setLoadedQueryKey("");
        setError(fetchError.message || "請款/對帳資料載入失敗");
      } finally {
        if (settlementRequestIdRef.current === requestId) setLoading(false);
      }
    },
    [appliedRange, crmApiBase, email, mdmAuthKey]
  );

  const fetchLatestBatchId = useCallback(async () => {
    if (!email) {
      setLatestBatchId("");
      setLatestBatchError("找不到登入者 Email");
      return;
    }

    try {
      const response = await fetch(`${crmApiBase}/api/v1/order/settle`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Mdm-Auth": mdmAuthKey,
        },
        body: JSON.stringify({ email }),
      });

      const json = await readApiResponse(response);
      if (!response.ok) {
        throw new Error(json?.message || `Request failed (${response.status})`);
      }

      setLatestBatchId(pickLatestBatchId(asArray(json?.data?.body)));
      setLatestBatchError("");
    } catch (fetchError) {
      console.error(fetchError);
      setLatestBatchId("");
      setLatestBatchError(fetchError.message || "可上傳批次讀取失敗");
    }
  }, [crmApiBase, email, mdmAuthKey]);

  useEffect(() => {
    fetchSettlements(appliedRange);
    fetchLatestBatchId();
  }, [appliedRange, fetchLatestBatchId, fetchSettlements]);

  const handleSearch = useCallback(() => {
    if ((filters.start && !filters.end) || (!filters.start && filters.end)) {
      setFeedback({ severity: "error", message: "請同時選擇開始日期與結束日期" });
      return;
    }
    if (hasCompleteRange(filters) && filters.end < filters.start) {
      setFeedback({ severity: "error", message: "結束日期不可早於開始日期" });
      return;
    }
    setFeedback(null);
    setAppliedRange({ ...filters });
  }, [filters]);

  const handleReset = useCallback(() => {
    const nextRange = createDefaultHistoryRange();
    setFeedback(null);
    setFilters(nextRange);
    setAppliedRange(nextRange);
  }, []);

  const refreshAll = useCallback(() => {
    fetchSettlements(appliedRange);
    fetchLatestBatchId();
  }, [appliedRange, fetchLatestBatchId, fetchSettlements]);

  const handleDownloadSettlements = useCallback(() => {
    if (!canDownloadSettlements) {
      setFeedback({ severity: "error", message: "請先完成目前條件的請款紀錄查詢" });
      return;
    }

    const headers = [
      "請款時間",
      "批次 ID",
      "批次訊息",
      "品牌",
      "付款商戶",
      "訂單 ID",
      "請款狀態",
      "訂單狀態",
      "金額",
      "實付金額",
      "發票號碼",
      "付款方式",
      "訂單時間",
      "門市 ID",
      "門市名稱",
      "訂單描述",
      "Email",
      "統編發票 Email",
      "Reference",
      "備註",
      "Memo",
    ];

    const body = settlementRows.map((row) => [
      formatDate(row.batchDate),
      row.batchId ?? "",
      row.batchMessage ?? "",
      row.dataMerchantCode ?? row.merchantId ?? "",
      row.dataMerchantCode ?? row.paymentMerchantId ?? row.merchantId ?? "",
      row.orderId ?? "",
      row.status ?? "",
      row.orderStatus ?? "",
      row.amount ?? "",
      row.paidAmount ?? "",
      row.invoiceNo ?? "",
      row.paymentMethod ?? "",
      formatDate(row.orderTime),
      row.storeId ?? "",
      row.storeName ?? "",
      row.description ?? "",
      row.email ?? "",
      row.vatEmail ?? "",
      row.reference ?? "",
      row.note ?? "",
      row.memo ?? "",
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...body]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Settlements");
    const fileLabel = hasCompleteRange(appliedRange)
      ? `${appliedRange.start}-${appliedRange.end}`
      : "default-range";
    XLSX.writeFile(workbook, `billing-reconcile-${fileLabel}.xlsx`);
    setFeedback({ severity: "success", message: `已下載 ${settlementRows.length} 筆請款紀錄` });
  }, [appliedRange, canDownloadSettlements, settlementRows]);

  const handleUploadLatestSettlement = useCallback(async () => {
    if (!email) {
      setFeedback({ severity: "error", message: "找不到登入者 Email" });
      return;
    }
    if (!latestBatchId) {
      setFeedback({ severity: "error", message: "目前沒有可上傳的 batchId" });
      return;
    }

    setUploadLoading(true);
    try {
      const response = await fetch(`${crmApiBase}/api/v1/order/settle_upload`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Mdm-Auth": mdmAuthKey,
        },
        body: JSON.stringify({
          email,
          batchId: latestBatchId,
        }),
      });

      const json = await readApiResponse(response);
      if (!response.ok) {
        throw new Error(json?.message || `Request failed (${response.status})`);
      }

      setFeedback({
        severity: "success",
        message: json?.data?.body?.message || json?.message || "後台上傳當日結算成功",
      });
      fetchSettlements(appliedRange);
      fetchLatestBatchId();
    } catch (uploadError) {
      console.error(uploadError);
      setFeedback({
        severity: "error",
        message: uploadError.message || "後台上傳當日結算失敗",
      });
    } finally {
      setUploadLoading(false);
    }
  }, [
    appliedRange,
    crmApiBase,
    email,
    fetchLatestBatchId,
    fetchSettlements,
    latestBatchId,
    mdmAuthKey,
  ]);

  const handleToggleAutoCapture = useCallback(
    async (event) => {
      const nextValue = event.target.checked;
      setAutoCaptureEnabled(nextValue);
      setAutoCaptureLoading(true);

      try {
        const response = await fetch(`${crmApiBase}/api/v1/order/auto_capture`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Mdm-Auth": mdmAuthKey,
          },
          body: JSON.stringify({
            email,
            enable: nextValue,
          }),
        });

        const json = await readApiResponse(response);
        if (!response.ok) {
          throw new Error(json?.message || `Request failed (${response.status})`);
        }

        setFeedback({
          severity: "success",
          message: `自動請款操作 ${json?.message || "成功"}`,
        });
      } catch (toggleError) {
        console.error(toggleError);
        setAutoCaptureEnabled(!nextValue);
        setFeedback({
          severity: "error",
          message: toggleError.message || "自動請款切換失敗",
        });
      } finally {
        setAutoCaptureLoading(false);
      }
    },
    [crmApiBase, email, mdmAuthKey]
  );

  const settlementTable = useMemo(() => {
    const columns = [
      {
        Header: "結算時間",
        accessor: "date",
        Cell: ({ value }) => dateCell(value),
      },
      {
        Header: "結算明細",
        accessor: "settlementsAction",
        align: "center",
        disableSortBy: true,
      },
      { Header: "批次 ID", accessor: "batchId" },
      { Header: "訊息", accessor: "message" },
      { Header: "任務資料", accessor: "tasksAction", align: "center", disableSortBy: true },
    ];

    const tableRows = rows.map((row, index) =>
      sanitizeDataTableRow({
        id: row?.batchId || `${index}`,
        date: row?.date ?? "",
        settlementsActionLabel: "點擊查看",
        settlementsAction: (
          <Button
            size="small"
            variant="outlined"
            sx={secondaryActionButtonSx}
            onClick={() => setSelectedSettlement(asArray(row?.settlements))}
          >
            點擊查看
          </Button>
        ),
        batchId: row?.batchId ?? "",
        message: row?.message ?? "",
        tasksActionLabel: Array.isArray(row?.tasks) ? `${row.tasks.length} 筆任務` : "查看",
        tasksAction:
          row?.tasks && (Array.isArray(row.tasks) ? row.tasks.length > 0 : true) ? (
            <Button
              size="small"
              variant="contained"
              sx={primaryActionButtonSx}
              onClick={() => setSelectedPayloadDetail(row?.tasks)}
            >
              {Array.isArray(row?.tasks) ? `查看 ${row.tasks.length} 筆` : "查看"}
            </Button>
          ) : (
            <MDTypography variant="caption" color="text">
              -
            </MDTypography>
          ),
      })
    );

    return { columns, rows: tableRows };
  }, [rows]);

  const settlementDetailTable = useMemo(() => {
    const columns = [
      { Header: "品牌", accessor: "merchantId" },
      { Header: "付款商戶", accessor: "paymentMerchantId" },
      { Header: "動作", accessor: "action" },
      { Header: "訂單 ID", accessor: "orderId" },
      { Header: "金額", accessor: "amount" },
      { Header: "發票號碼", accessor: "invoiceNo" },
      { Header: "狀態", accessor: "status" },
      { Header: "訊息", accessor: "message" },
      { Header: "批次 ID", accessor: "batchId" },
      {
        Header: "建立時間",
        accessor: "createdAt",
        Cell: ({ value }) => dateCell(value),
      },
      { Header: "付款方式", accessor: "paymentMethod" },
      {
        Header: "訂單時間",
        accessor: "orderTime",
        Cell: ({ value }) => dateCell(value),
      },
      {
        Header: "更多",
        accessor: "detailAction",
        align: "center",
        disableSortBy: true,
      },
    ];

    const tableRows = asArray(selectedSettlement).map((row, index) =>
      sanitizeDataTableRow({
        id: row?.orderId || `${index}`,
        merchantId: row?.dataMerchantCode ?? row?.merchantId ?? "",
        paymentMerchantId: row?.dataMerchantCode ?? row?.paymentMerchantId ?? row?.merchantId ?? "",
        action: row?.action ?? "",
        orderId: row?.orderId ?? "",
        amount: row?.amount ?? "",
        invoiceNo: row?.invoiceNo ?? "",
        status: row?.status ?? "",
        message: row?.message ?? "",
        batchId: row?.batchId ?? "",
        createdAt: row?.createdAt ?? "",
        paymentMethod: row?.paymentMethod ?? "",
        orderTime: row?.orderTime ?? "",
        detailAction: (
          <Button
            size="small"
            variant="contained"
            sx={primaryActionButtonSx}
            onClick={() => setSelectedPayloadDetail(row)}
          >
            查看明細
          </Button>
        ),
      })
    );

    return { columns, rows: tableRows };
  }, [selectedSettlement]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  <RoutePageTitle />
                </MDTypography>
              </MDBox>
              <MDBox p={3}>
                <Stack spacing={2}>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                    <TextField
                      label="開始日期"
                      type="date"
                      size="small"
                      value={filters.start}
                      onChange={(event) =>
                        setFilters((prev) => ({ ...prev, start: event.target.value }))
                      }
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                    <TextField
                      label="結束日期"
                      type="date"
                      size="small"
                      value={filters.end}
                      onChange={(event) =>
                        setFilters((prev) => ({ ...prev, end: event.target.value }))
                      }
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                  </Stack>

                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems={{ xs: "stretch", md: "center" }}
                  >
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                      <Button variant="contained" sx={primaryActionButtonSx} onClick={handleSearch}>
                        查詢
                      </Button>
                      <Button
                        variant="outlined"
                        sx={secondaryActionButtonSx}
                        onClick={handleReset}
                        disabled={loading}
                      >
                        重設
                      </Button>
                      <Button
                        variant="outlined"
                        sx={secondaryActionButtonSx}
                        onClick={handleDownloadSettlements}
                        disabled={!canDownloadSettlements}
                      >
                        下載完整紀錄
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleUploadLatestSettlement}
                        disabled={uploadLoading || !latestBatchId}
                        sx={primaryActionButtonSx}
                      >
                        {uploadLoading ? "上傳中..." : "後台上傳當日結算"}
                      </Button>
                      <Button
                        variant="outlined"
                        sx={secondaryActionButtonSx}
                        onClick={refreshAll}
                        disabled={loading}
                      >
                        {loading ? "刷新中..." : "重新整理"}
                      </Button>
                    </Stack>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={autoCaptureEnabled}
                          onChange={handleToggleAutoCapture}
                          disabled={autoCaptureLoading || !email}
                        />
                      }
                      label="自動請款功能"
                    />
                  </Stack>

                  <Alert severity="info">
                    {`查詢區間: ${getRangeSummaryLabel(appliedRange)} / 批次數: ${
                      rows.length
                    } / 明細筆數: ${settlementRows.length} / 最新可上傳 batchId: ${
                      latestBatchId || "-"
                    } / 使用者 Email: ${email || "-"}`}
                  </Alert>

                  {feedback ? (
                    <Alert severity={feedback.severity} onClose={() => setFeedback(null)}>
                      {feedback.message}
                    </Alert>
                  ) : null}

                  {latestBatchError ? <Alert severity="warning">{latestBatchError}</Alert> : null}

                  {error ? <Alert severity="error">{error}</Alert> : null}

                  {loading ? (
                    <MDBox display="flex" justifyContent="center" py={4}>
                      <CircularProgress />
                    </MDBox>
                  ) : (
                    <DataTable
                      table={settlementTable}
                      entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50] }}
                      canSearch
                      showTotalEntries={false}
                      isSorted
                      noEndBorder
                    />
                  )}
                </Stack>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog
        open={Boolean(selectedSettlement)}
        onClose={() => setSelectedSettlement(null)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>結算明細</DialogTitle>
        <DialogContent>
          {settlementDetailTable.rows.length > 0 ? (
            <DataTable
              table={settlementDetailTable}
              entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50] }}
              canSearch
              showTotalEntries={false}
              isSorted
              noEndBorder
            />
          ) : (
            <Alert severity="info">無結算明細</Alert>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(selectedPayloadDetail)}
        onClose={() => setSelectedPayloadDetail(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>資料明細</DialogTitle>
        <DialogContent>
          {selectedPayloadDetail ? (
            <Stack spacing={2}>
              {Array.isArray(selectedPayloadDetail) ? (
                selectedPayloadDetail.map((item, index) => (
                  <MDBox key={item?.id || index} p={2} border="1px solid #e0e0e0" borderRadius="lg">
                    <Stack spacing={1}>
                      <MDTypography variant="button" fontWeight="bold">
                        {item?.name || `項目 ${index + 1}`}
                      </MDTypography>
                      <MDTypography variant="caption">
                        狀態：{formatValue(item?.status)}
                      </MDTypography>
                      <MDTypography variant="caption">
                        建立時間：{formatDate(item?.createdAt)}
                      </MDTypography>
                      <MDTypography variant="caption">
                        檔名：{formatValue(item?.fileName)}
                      </MDTypography>
                      <MDTypography variant="caption">
                        路徑：{formatValue(item?.filePath)}
                      </MDTypography>
                      <MDTypography variant="caption">
                        訊息：{formatValue(item?.message)}
                      </MDTypography>
                      {Array.isArray(item?.logs) && item.logs.length > 0 ? (
                        <>
                          <Divider />
                          <MDTypography variant="button" fontWeight="medium">
                            執行紀錄
                          </MDTypography>
                          {item.logs.map((log, logIndex) => (
                            <MDBox key={logIndex} pl={1}>
                              <MDTypography variant="caption">
                                {formatDate(log?.createdAt)} / {formatValue(log?.status)} /{" "}
                                {formatValue(log?.message)}
                              </MDTypography>
                            </MDBox>
                          ))}
                        </>
                      ) : null}
                    </Stack>
                  </MDBox>
                ))
              ) : (
                <MDBox p={2} border="1px solid #e0e0e0" borderRadius="lg">
                  <Stack spacing={1}>
                    <MDTypography variant="button" fontWeight="bold">
                      {formatValue(selectedPayloadDetail?.name)}
                    </MDTypography>
                    <MDTypography variant="caption">
                      狀態：{formatValue(selectedPayloadDetail?.status)}
                    </MDTypography>
                    <MDTypography variant="caption">
                      建立時間：{formatDate(selectedPayloadDetail?.createdAt)}
                    </MDTypography>
                    <MDTypography variant="caption">
                      檔名：{formatValue(selectedPayloadDetail?.fileName)}
                    </MDTypography>
                    <MDTypography variant="caption">
                      路徑：{formatValue(selectedPayloadDetail?.filePath)}
                    </MDTypography>
                    <MDTypography variant="caption">
                      訊息：{formatValue(selectedPayloadDetail?.message)}
                    </MDTypography>
                    {Array.isArray(selectedPayloadDetail?.logs) &&
                    selectedPayloadDetail.logs.length > 0 ? (
                      <>
                        <Divider />
                        <MDTypography variant="button" fontWeight="medium">
                          執行紀錄
                        </MDTypography>
                        {selectedPayloadDetail.logs.map((log, logIndex) => (
                          <MDBox key={logIndex} pl={1}>
                            <MDTypography variant="caption">
                              {formatDate(log?.createdAt)} / {formatValue(log?.status)} /{" "}
                              {formatValue(log?.message)}
                            </MDTypography>
                          </MDBox>
                        ))}
                      </>
                    ) : null}
                  </Stack>
                </MDBox>
              )}
            </Stack>
          ) : null}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

export default BillingReconcileCM;
